import { Check, ImageIcon, Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import ImageInput from './Shared/ImageInput/ImageInput';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { FormItem, FormLabel, FormDescription } from '@/Domain/components/Shared/Form/Form';
import { cn } from '@/Domain/utils/cn';

type Props = {
  label: string;
  description: string;
  currentImage: string | null;
  validation: string;
  upload_image: string;
  onChange: (newImage: string | null) => void;
  testId?: string;
  onUseDefault?: () => void;
  use_default_label?: string;
  use_default_image?: string;
};

export const ImageUpload = ({
  label,
  description,
  validation,
  upload_image,
  currentImage,
  onChange,
  testId,
  onUseDefault,
  use_default_label,
  use_default_image,
}: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [justDeleted, setJustDeleted] = useState(false);
  const deleteFeedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (deleteFeedbackTimer.current) clearTimeout(deleteFeedbackTimer.current);
    },
    [],
  );

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDelete = () => {
    onChange(null);
    // Briefly show a confirmation checkmark, like the account-settings profile picture.
    setJustDeleted(true);
    if (deleteFeedbackTimer.current) clearTimeout(deleteFeedbackTimer.current);
    deleteFeedbackTimer.current = setTimeout(() => setJustDeleted(false), 1500);
  };

  return (
    <FormItem data-testid={testId}>
      <FormLabel data-testid={testId ? `${testId}-label` : undefined}>{label}</FormLabel>
      <FormDescription data-testid={testId ? `${testId}-description` : undefined}>
        {description}
      </FormDescription>
      <div className="flex items-center gap-4">
        {/* Profile-picture-style preview: hover reveals a centered upload overlay and a
            delete button in the top-left corner. */}
        <div className="relative group size-20 shrink-0">
          <div className="size-20 rounded-full border border-border overflow-hidden flex items-center justify-center bg-muted">
            {currentImage ? (
              <img
                data-testid="image-upload-img"
                src={currentImage}
                alt="Preview"
                className="size-full object-cover"
              />
            ) : (
              <ImageIcon
                data-testid="image-upload-placeholder"
                className="h-7 w-7 text-foreground-subtle"
              />
            )}
          </div>
          <button
            type="button"
            data-testid={testId ? `${testId}-button` : undefined}
            onClick={handleUploadClick}
            aria-label={upload_image}
            className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer focus-visible:opacity-100 focus:outline-none"
          >
            <Upload className="h-6 w-6 text-white" />
          </button>
          {(currentImage || justDeleted) && (
            <button
              type="button"
              data-testid="image-upload-delete"
              onClick={handleDelete}
              aria-label="Delete"
              className={cn(
                'absolute -top-1 -left-1 z-10 size-6 rounded-full text-destructive-foreground border border-background flex items-center justify-center transition-[opacity,background-color] cursor-pointer focus-visible:opacity-100 focus:outline-none',
                justDeleted
                  ? 'opacity-100 bg-success text-success-foreground hover:bg-success'
                  : 'opacity-0 group-hover:opacity-100 bg-destructive hover:bg-destructive/90',
              )}
            >
              {justDeleted ? <Check className="h-3.5 w-3.5" /> : <Trash2 className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
        {onUseDefault && (
          <Button
            data-testid={testId ? `${testId}-use-default` : undefined}
            variant="secondary"
            size="sm"
            onClick={onUseDefault}
          >
            {use_default_image && (
              <img src={use_default_image} alt="" className="size-4 rounded-full object-cover" />
            )}
            {use_default_label}
          </Button>
        )}
        <ImageInput
          ref={fileInputRef}
          onChange={onChange}
          onError={(error) => toast.error(error.message)}
          accept="image/png, image/jpeg, image/webp, image/svg+xml"
          className="hidden"
        />
      </div>
      <p
        data-testid={testId ? `${testId}-validation` : undefined}
        className="text-xs text-foreground-subtle"
      >
        {validation}
      </p>
    </FormItem>
  );
};
